import { countOpenInvitations } from "@/api/invitations/invitationState";
import { useOrganizationInvitations } from "@/api/invitations/useInvitations";
import {
  activeCountLabel,
  activeMemberCount,
  filterDirectory,
  filterOrgMembers,
  memberRowAccessibilityLabel,
  roleLabel,
  sortOrgRoles,
  type MemberListFilter,
} from "@/api/members/memberList";
import {
  useOrgMemberDirectory,
  useOrgMembers,
} from "@/api/members/useOrgMembers";
import { ORG_MEMBERSHIP_ROLES } from "@/auth/membershipRoles";
import { useOrgPermissions } from "@/auth/orgPermissions";
import { routes } from "@/constants/routes";
import { useNetworkStatus } from "@/network/NetworkContext";
import { useTheme } from "@/theme/ThemeProvider";
import { AppButton } from "@/ui/components/AppButton";
import { ClubSection } from "@/ui/components/ClubSection";
import { FloatingLabelInput } from "@/ui/components/FloatingLabelInput";
import { HIT_TARGET_MIN } from "@/ui/components/listRowA11y";
import { ListRow } from "@/ui/components/ListRow";
import { OptionChipGroup } from "@/ui/components/OptionChipGroup";
import { Screen } from "@/ui/components/Screen";
import { StatusChip } from "@/ui/components/StatusChip";
import { EmptyState, LoadingState } from "@/ui/components/StateViews";
import { useLocalSearchParams, useRouter } from "expo-router";
import { MagnifyingGlassIcon, UserPlusIcon } from "phosphor-react-native";
import { useEffect, useMemo, useState } from "react";
import { RefreshControl, Text, View } from "react-native";

const DIRECTORY_HINT =
  "Du siehst Namen und Rollen der aktiven Mitglieder. Kontaktdaten, Einladungen und Rollenverwaltung sind der Administration und dem Vorstand vorbehalten.";
const OFFLINE_HINT =
  "Du bist offline. Mitgliederverwaltung ist nur online möglich.";

function param(value: string | string[] | undefined): string {
  if (typeof value === "string") return value;
  return value?.[0] ?? "";
}

export default function TenantMembersScreen() {
  const tenantID = param(useLocalSearchParams<{ tenantID: string }>().tenantID);
  const router = useRouter();
  const { theme } = useTheme();
  const { isOnline } = useNetworkStatus();
  const permissions = useOrgPermissions(tenantID || undefined);
  const manage = !permissions.loading && permissions.canManageMembers;
  const readDirectory =
    !permissions.loading && !permissions.canManageMembers && permissions.isMember;
  const listQuery = useOrgMembers(manage ? tenantID : undefined);
  const directoryQuery = useOrgMemberDirectory(readDirectory ? tenantID : undefined);
  const invitations = useOrganizationInvitations(manage ? tenantID : undefined);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<MemberListFilter>("all");

  const orgName = permissions.orgName?.trim() || "Verein";
  const loading =
    permissions.loading ||
    (manage && listQuery.isLoading) ||
    (readDirectory && directoryQuery.isLoading);
  const error = manage ? listQuery.error : readDirectory ? directoryQuery.error : null;
  const reload = () => {
    if (manage) void listQuery.refetch();
    else void directoryQuery.refetch();
  };

  const managedRows = useMemo(
    () => filterOrgMembers(listQuery.data ?? [], search, filter),
    [listQuery.data, search, filter],
  );
  const directoryRows = useMemo(
    () => filterDirectory(directoryQuery.data ?? [], search),
    [directoryQuery.data, search],
  );
  const sourceCount = manage
    ? (listQuery.data?.length ?? 0)
    : (directoryQuery.data?.length ?? 0);
  const visibleCount = manage ? managedRows.length : directoryRows.length;
  const activeCount = manage
    ? activeMemberCount(listQuery.data ?? [])
    : (directoryQuery.data?.length ?? 0);

  const openInviteCount =
    invitations.isError || invitations.isLoading
      ? null
      : countOpenInvitations(invitations.data ?? [], Date.now());

  useEffect(() => {
    if (invitations.error) console.error("open invitations", invitations.error);
  }, [invitations.error]);

  const resetFilters = () => {
    setSearch("");
    setFilter("all");
  };

  const openClub = () => router.replace(routes.myClub as never);
  const openInvite = () => {
    if (!isOnline) return;
    router.push(routes.tenant.membersInvite(tenantID) as never);
  };

  return (
    <Screen
      title="Verein"
      scroll
      contentStyle={{ gap: theme.space.md }}
      refreshControl={
        <RefreshControl
          refreshing={Boolean(listQuery.isFetching || directoryQuery.isFetching) && !loading}
          onRefresh={reload}
          tintColor={theme.color.primary}
        />
      }
    >
      <ClubSection
        orgName={orgName}
        roles={permissions.roles}
        showSuperadminChip={permissions.isReadOnlySuperadmin}
        active="members"
        onSelect={(tab) => {
          if (tab === "posts") openClub();
        }}
        headerAction={
          manage && isOnline ? (
            <AppButton
              icon={UserPlusIcon}
              accessibilityRole="button"
              accessibilityLabel={`Person in ${orgName} einladen`}
              labelStyle={{ flexShrink: 1 }}
              onPress={openInvite}
              style={{
                alignSelf: "flex-start",
                maxWidth: "100%",
                minHeight: HIT_TARGET_MIN,
              }}
            >
              Einladen
            </AppButton>
          ) : null
        }
      >
        <View style={{ gap: theme.space.md }}>
          {!isOnline ? (
            <Text style={{ color: theme.color.muted }}>{OFFLINE_HINT}</Text>
          ) : null}

          {readDirectory ? (
            <Text style={{ color: theme.color.muted, lineHeight: 20 }}>
              {DIRECTORY_HINT}
            </Text>
          ) : null}

          {loading ? (
            <LoadingState message="Mitglieder werden geladen …" />
          ) : error ? (
            <View
              accessibilityLiveRegion="polite"
              style={{ gap: theme.space.sm }}
            >
              <Text style={{ color: theme.color.danger, fontWeight: "700" }}>
                Die Mitgliederliste konnte nicht geladen werden.
              </Text>
              <Text style={{ color: theme.color.muted }}>
                Bitte versuche es gleich noch einmal. Bleibt das Problem, wende dich an die Administration.
              </Text>
              <AppButton
                variant="secondary"
                onPress={reload}
                style={{ alignSelf: "flex-start", minHeight: HIT_TARGET_MIN }}
              >
                Erneut laden
              </AppButton>
            </View>
          ) : sourceCount === 0 ? (
            <EmptyState
              title="Noch keine weiteren Mitglieder"
              message={
                manage
                  ? "Lade Personen aus deinem Verein ein, damit ihr gemeinsam dokumentieren könnt."
                  : undefined
              }
            />
          ) : (
            <View style={{ gap: theme.space.md }}>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: theme.space.sm,
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: 0,
                }}
              >
                <MagnifyingGlassIcon size={20} color={theme.color.muted} />
                <FloatingLabelInput
                  label={manage ? "Name oder E-Mail" : "Name"}
                  value={search}
                  onChangeText={setSearch}
                  containerStyle={{ flex: 1, minWidth: 0, maxWidth: "100%" }}
                />
              </View>

              {manage ? (
                <OptionChipGroup
                  allowDeselect={false}
                  hitTargetMin={HIT_TARGET_MIN}
                  value={filter}
                  onChange={(value) => setFilter(value ?? "all")}
                  options={[
                    { value: "all", label: "Alle" },
                    ...ORG_MEMBERSHIP_ROLES.map((role) => ({
                      value: role,
                      label: roleLabel(role),
                    })),
                    { value: "disabled", label: "Deaktiviert" },
                  ]}
                />
              ) : null}

              <Text style={{ color: theme.color.muted, fontWeight: "600" }}>
                {activeCountLabel(activeCount)}
              </Text>

              {manage ? (
                <ListRow
                  title="Offene Einladungen"
                  subtitle="Verwalten"
                  badge={
                    openInviteCount == null
                      ? undefined
                      : openInviteCount === 0
                        ? "Keine offen"
                        : `${openInviteCount} offen`
                  }
                  badgeTone="neutral"
                  entity="person"
                  enforceHitTarget
                  accessibilityLabel={
                    openInviteCount == null
                      ? "Offene Einladungen, Verwalten"
                      : `Offene Einladungen, ${
                          openInviteCount === 0 ? "Keine offen" : `${openInviteCount} offen`
                        }, Verwalten`
                  }
                  onPress={isOnline ? openInvite : undefined}
                />
              ) : null}

              {visibleCount === 0 ? (
                <EmptyState
                  title="Keine Treffer"
                  action={
                    <AppButton
                      variant="secondary"
                      onPress={resetFilters}
                      style={{ minHeight: HIT_TARGET_MIN }}
                    >
                      Filter zurücksetzen
                    </AppButton>
                  }
                />
              ) : manage ? (
                managedRows.map((member) => {
                  const name = member.displayName?.trim() || "Unbenannt";
                  return (
                    <ListRow
                      key={member.profileId}
                      entity="person"
                      title={name}
                      subtitle={member.email}
                      enforceHitTarget
                      accessibilityLabel={memberRowAccessibilityLabel({
                        name,
                        email: member.email,
                        roleLabels: sortOrgRoles(member.roles).map(roleLabel),
                        includeEmail: true,
                        deactivated: member.status === "disabled",
                      })}
                      onPress={() =>
                        router.push(
                          routes.tenant.memberDetail(tenantID, member.profileId) as never,
                        )
                      }
                    >
                      <View
                        style={{
                          flexDirection: "row",
                          flexWrap: "wrap",
                          gap: theme.space.xs,
                          marginTop: theme.space.xs,
                        }}
                      >
                        {sortOrgRoles(member.roles).map((role) => (
                          <StatusChip
                            key={role}
                            label={roleLabel(role)}
                            tone="neutral"
                            size="sm"
                          />
                        ))}
                        {member.status === "disabled" ? (
                          <StatusChip label="Deaktiviert" tone="neutral" size="sm" />
                        ) : null}
                        {member.status === "invited" ? (
                          <StatusChip label="Eingeladen" tone="neutral" size="sm" />
                        ) : null}
                      </View>
                    </ListRow>
                  );
                })
              ) : (
                directoryRows.map((member) => {
                  const name = member.displayName?.trim() || "Unbenannt";
                  return (
                    <ListRow
                      key={member.profileId}
                      entity="person"
                      title={name}
                      accessibilityLabel={memberRowAccessibilityLabel({
                        name,
                        roleLabels: sortOrgRoles(member.roles).map(roleLabel),
                      })}
                    >
                      <View
                        style={{
                          flexDirection: "row",
                          flexWrap: "wrap",
                          gap: theme.space.xs,
                          marginTop: theme.space.xs,
                        }}
                      >
                        {sortOrgRoles(member.roles).map((role) => (
                          <StatusChip
                            key={role}
                            label={roleLabel(role)}
                            tone="neutral"
                            size="sm"
                          />
                        ))}
                      </View>
                    </ListRow>
                  );
                })
              )}
            </View>
          )}
        </View>
      </ClubSection>
    </Screen>
  );
}
